import React, { useState, useEffect, useRef } from 'react';
import { 
  makeStyles, 
  mergeClasses,
  tokens, 
  Button, 
  Card,
  Body1,
  Caption1,
  Title1,
  Title3,
  Subtitle2,
  Link,
  Tooltip,
  Badge
} from '@fluentui/react-components';
import { ArrowLeft20Regular,
  ChartMultiple20Regular,
  DataBarVertical20Regular,
  PeopleCommunity20Regular,
  Trophy20Regular,
  ArrowUpload20Regular,
  Document20Regular,
} from '@fluentui/react-icons';
import { dataVisualizationTokens, colorTokens } from '../../../tokens';
import { LLMSurveyAnalyzer } from '../../../utils/LLMSurveyAnalyzer';
import { parseQuestionNumbers, extractQuestionTexts } from '../../../utils/questionMapper';
import type { AnalyzedInsights, ChartDataItem } from '../../../types/forms';
import {
  FluentSentimentBarChart,
  FluentPieChart,
  FigmaHorizontalBarChart,
  FluentThemeCards,
  FluentThemeTable,
} from './FluentRechartsComponents';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// Helper function to get color from token path
const getVisualizationColor = (colorPath: string | undefined, fallbackIndex: number): string => {
  if (!colorPath) {
    return dataVisualizationTokens.qualitative[`slot${(fallbackIndex % 10) + 1}` as keyof typeof dataVisualizationTokens.qualitative];
  }
  
  const parts = colorPath.split('.');
  if (parts.length === 2) {
    const [category, slot] = parts;
    if (category === 'qualitative' && slot in dataVisualizationTokens.qualitative) {
      return dataVisualizationTokens.qualitative[slot as keyof typeof dataVisualizationTokens.qualitative];
    }
    if (category === 'semantic' && slot in dataVisualizationTokens.semantic) {
      return dataVisualizationTokens.semantic[slot as keyof typeof dataVisualizationTokens.semantic];
    }
    if (category === 'sequential') {
      const paletteFamily = parts[2];
      if (paletteFamily && paletteFamily in dataVisualizationTokens.sequential) {
        return dataVisualizationTokens.sequential[paletteFamily as keyof typeof dataVisualizationTokens.sequential].primary;
      }
    }
  }
  
  return dataVisualizationTokens.qualitative[`slot${(fallbackIndex % 10) + 1}` as keyof typeof dataVisualizationTokens.qualitative];
};

// Helper: Transform insight data items for bar charts
const transformBarChartData = (items: ChartDataItem[]) => {
  return items.map(item => ({
    label: item.label || item.name || 'Unknown',
    value: typeof item.value === 'number' ? item.value : parseFloat(String(item.value)) || 0,
    color: item.color ? getVisualizationColor(item.color, 0) : getVisualizationColor('', 0),
  }));
};

// Helper: Render bar chart based on type
const renderBarChart = (_type: string, items: ChartDataItem[]) => {
  const data = transformBarChartData(items);
  const showPercentage = items.some(item => item.percentage !== undefined);
  
  return (
    <FigmaHorizontalBarChart
      data={data}
      showPercentage={showPercentage}
      noWrapper
    />
  );
};

const useStyles = makeStyles({
  container: {
    width: '100%',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: '8px',
  },
  
  // Header section with back button
  header: {
    backgroundColor: '#F9FAFC',
    padding: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXXL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  
  backButton: {
    minWidth: 'auto',
  },
  
  // Scrollable content area
  scrollContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: tokens.spacingVerticalXXXL,
    paddingLeft: tokens.spacingHorizontalXXXL,
    paddingRight: tokens.spacingHorizontalXXXL,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    backgroundColor: '#F9FAFC',
    '::-webkit-scrollbar': {
      width: '8px',
    },
    '::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '::-webkit-scrollbar-thumb': {
      backgroundColor: tokens.colorNeutralStroke2,
      borderRadius: tokens.borderRadiusSmall,
    },
    '::-webkit-scrollbar-thumb:hover': {
      backgroundColor: tokens.colorNeutralStroke1,
    },
  },
  
  // Content container with max width
  contentContainer: {
    width: '100%',
    maxWidth: '1100px',
    paddingTop: '40px',
    paddingLeft: '40px',
    paddingRight: '40px',
    paddingBottom: '80px', // Extra bottom padding to ensure content is not cut off
  },
  
  // Report title and subtitle
  reportHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalXL,
  },
  
  // Embedded report header (shown in tab mode)
  embeddedReportHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: tokens.spacingVerticalXXXL,
    paddingBottom: tokens.spacingVerticalXL,
  },
  
  embeddedReportHeaderLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    flex: 1,
  },
  
  embeddedReportTitle: {
    fontSize: '24px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    lineHeight: '32px',
  },
  
  embeddedReportDescription: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
    lineHeight: '18px',
  },
  
  dataSnapshot: {
    fontSize: '13px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'nowrap',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusXLarge,
  },
  
  reportSubtitle: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  
  // Section headers
  sectionHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalL,
    marginTop: tokens.spacingVerticalXXXL,
  },
  
  sectionTitle: {
    fontSize: '24px',
    lineHeight: '32px',
  },
  
  sectionSubtitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  
  reportTitle: {
    marginBottom: tokens.spacingVerticalXS,
    fontSize: '32px',
    lineHeight: '40px',
  },
  
  // Data overview cards grid
  overviewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)', // Always 4 columns in one row
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalXXL,
  },
  
  overviewCard: {
    height: '100%',
    borderRadius: '16px', // borderRadiusFormSpecial
  },
  
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
  },
  
  cardIcon: {
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalXS,
  },
  
  cardLabel: {
    fontSize: tokens.fontSizeBase400, // 16px
    color: tokens.colorNeutralForeground2,
  },
  
  cardValue: {
    fontSize: '24px', // Smaller for text-based values
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: '32px',
    color: '#155DFC', // Blue color for data values
  },
  
  cardValueLarge: {
    fontSize: '32px', // For pure numbers (99%, 4.52/5)
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: '40px',
    color: '#155DFC', // Blue color for data values
  },
  
  cardDescription: {
    color: tokens.colorNeutralForeground2,
  },
  
  // Gradient text for emphasized keywords in titles
  gradientText: {
    background: colorTokens.formsBrandGradient,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  
  // Insights section
  insightsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  
  insightCard: {
    paddingTop: '36px',
    paddingBottom: '36px',
    paddingLeft: '30px',
    paddingRight: '30px',
    display: 'block', // Force block layout to allow child height calculation
    borderRadius: '16px', // borderRadiusFormSpecial
    minHeight: 'auto', // Auto height based on content
  },
  
  // Wrapper for the entire insight content with hanging number
  insightTitleWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  
  // The number that hangs to the left - fixed width for consistent alignment
  insightNumber: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: '20px',
    lineHeight: '28px',
    color: tokens.colorNeutralForeground1,
    marginRight: '0px',
    flexShrink: 0,
    width: '28px',  // Fixed width to ensure consistent alignment
    minWidth: '28px',
  },
  
  // Container for title text + badge + explanation (all aligned)
  insightTextBlock: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
  },
  
  insightHeader: {
    marginBottom: tokens.spacingVerticalS,
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    flexWrap: 'wrap',
  },
  
  // The title text (without the number)
  insightTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: '20px',
    lineHeight: '28px',
    color: tokens.colorNeutralForeground1,
  },
  
  questionBadge: {
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '4px',
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    cursor: 'help',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
  },

  // Container for content that should align with title text (after the number)
  insightContent: {
    paddingLeft: '0', // Now handled by insightTextBlock
  },
  
  // Unified chart wrapper - consistent styling for all chart types
  chartWrapper: {
    backgroundColor: '#F8FAFC',
    border: `1px solid ${tokens.colorNeutralStroke3}`,
    borderRadius: '12px',
    padding: '24px',
  },
  
  insightSubtitle: {
    display: 'block',
    marginBottom: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  
  insightExplanation: {
    marginBottom: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground2,
  },
  
  // Data table for insights
  dataTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: tokens.spacingVerticalM,
  },
  
  tableHeader: {
    textAlign: 'left',
    padding: tokens.spacingVerticalM,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    borderBottom: `2px solid ${tokens.colorNeutralStroke2}`,
  },
  
  tableCell: {
    padding: tokens.spacingVerticalM,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  
  tableCellRight: {
    padding: tokens.spacingVerticalM,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    textAlign: 'right',
  },
  
  // View link
  viewLinkContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  
  // ========================================
  // Figma-style Insight Badge Card (Pattern)
  // Based on Fluent Data Viz Color node-id=302-40468
  // ========================================
  insightBadgeCard: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    gap: tokens.spacingHorizontalM,
    minHeight: '48px',
    marginTop: tokens.spacingVerticalL,
  },
  
  insightBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    backgroundColor: tokens.colorPaletteLavenderBackground2,
    borderRadius: tokens.borderRadiusSmall,
    flexShrink: 0,
  },
  
  insightBadgeText: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorPaletteLavenderForeground2,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  
  insightBadgeDescription: {
    flex: 1,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase300,
  },
  
  insightBadgeLink: {
    flexShrink: 0,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorBrandForeground1,
    textDecoration: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  
  // ========================================
  // User Quotation Styles
  // For open-text/subjective question insights
  // ========================================
  quotationsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    paddingTop: '20px', // Align with chart margin-top (20px in BarChart)
  },
  
  // New layout for subjective questions - side by side columns
  quotationsGridContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalL,
    marginTop: '20px',
    paddingLeft: '10px',
    paddingRight: '10px',
  },
  
  quotationColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    borderRadius: '16px',
  },
  
  quotationColumnPositive: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
  },
  
  quotationColumnNegative: {
    backgroundColor: tokens.colorPaletteRedBackground1,
  },
  
  quotationSectionLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: tokens.spacingVerticalXS,
  },
  
  quotationSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
  },
  
  quotationSectionEmoji: {
    fontSize: '24px',
    lineHeight: '1',
  },
  
  quotationSectionTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  
  quotationSectionLabelPositive: {
    color: dataVisualizationTokens.semantic.positive,
  },
  
  quotationSectionLabelNegative: {
    color: dataVisualizationTokens.semantic.negative,
  },
  
  quotationCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '12px',
    marginBottom: tokens.spacingVerticalXS,
  },
  
  quotationCardPositive: {
    // No border needed in new layout
  },
  
  quotationCardNegative: {
    // No border needed in new layout
  },
  
  quotationTheme: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase300,
  },
  
  quotationCounter: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
    marginLeft: tokens.spacingHorizontalXXS,
  },
  
  quotationText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    lineHeight: tokens.lineHeightBase300,
    fontStyle: 'italic',
    '::before': {
      content: '"\\201C"', // Opening quote
    },
    '::after': {
      content: '"\\201D"', // Closing quote
    },
  },
  
  quotationBullet: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  
  // Dynamic charts loading animation - carousel style
  chartsLoader: {
    position: 'relative',
    width: '120px',
    height: '100px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  // Carousel item wrapper - each with separate animation
  chartCarouselItem: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    animationDuration: '9s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
  },
  
  // Item 1: visible 0-33%, hidden 33-100%
  chartItem1: {
    animationName: {
      '0%': { opacity: 1, transform: 'scale(1)' },
      '30%': { opacity: 1, transform: 'scale(1)' },
      '33%': { opacity: 0, transform: 'scale(0.8)' },
      '97%': { opacity: 0, transform: 'scale(0.8)' },
      '100%': { opacity: 1, transform: 'scale(1)' },
    },
  },
  
  // Item 2: hidden 0-33%, visible 33-66%, hidden 66-100%
  chartItem2: {
    animationName: {
      '0%': { opacity: 0, transform: 'scale(0.8)' },
      '30%': { opacity: 0, transform: 'scale(0.8)' },
      '33%': { opacity: 1, transform: 'scale(1)' },
      '63%': { opacity: 1, transform: 'scale(1)' },
      '66%': { opacity: 0, transform: 'scale(0.8)' },
      '100%': { opacity: 0, transform: 'scale(0.8)' },
    },
  },
  
  // Item 3: hidden 0-66%, visible 66-100%
  chartItem3: {
    animationName: {
      '0%': { opacity: 0, transform: 'scale(0.8)' },
      '63%': { opacity: 0, transform: 'scale(0.8)' },
      '66%': { opacity: 1, transform: 'scale(1)' },
      '97%': { opacity: 1, transform: 'scale(1)' },
      '100%': { opacity: 0, transform: 'scale(0.8)' },
    },
  },
  
  // Bar chart animation
  barChartLoader: {
    display: 'flex',
    gap: '6px',
    alignItems: 'flex-end',
    height: '80px',
  },
  
  barLoaderItem: {
    width: '14px',
    borderRadius: '4px 4px 0 0',
    animationName: {
      '0%, 100%': { height: '20px' },
      '50%': { height: '80px' },
    },
    animationDuration: '1.5s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
  },
  
  bar1: {
    backgroundColor: '#637CEF',
    animationDelay: '0s',
  },
  
  bar2: {
    backgroundColor: '#E3008C',
    animationDelay: '0.2s',
  },
  
  bar3: {
    backgroundColor: '#00B7C3',
    animationDelay: '0.4s',
  },
  
  bar4: {
    backgroundColor: '#FFB900',
    animationDelay: '0.6s',
  },
  
  // Donut chart animation
  donutLoader: {
    position: 'relative',
    width: '80px',
    height: '80px',
  },
  
  donutRing: {
    position: 'absolute',
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    border: '12px solid #f0f0f0',
  },
  
  donutSegment: {
    position: 'absolute',
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    border: '12px solid transparent',
    borderTopColor: '#637CEF',
    animationName: {
      '0%': { transform: 'rotate(0deg)' },
      '100%': { transform: 'rotate(360deg)' },
    },
    animationDuration: '1.5s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'linear',
  },
  
  donutSegment2: {
    position: 'absolute',
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    border: '12px solid transparent',
    borderTopColor: '#E3008C',
    animationName: {
      '0%': { transform: 'rotate(120deg)' },
      '100%': { transform: 'rotate(480deg)' },
    },
    animationDuration: '1.5s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'linear',
  },
  
  donutSegment3: {
    position: 'absolute',
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    border: '12px solid transparent',
    borderTopColor: '#00B7C3',
    animationName: {
      '0%': { transform: 'rotate(240deg)' },
      '100%': { transform: 'rotate(600deg)' },
    },
    animationDuration: '1.5s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'linear',
  },
  
  donutCenter: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: 'white',
  },
  
  // Line chart animation
  lineChartLoader: {
    position: 'relative',
    width: '100px',
    height: '80px',
    overflow: 'hidden',
  },
  
  lineChartPath: {
    position: 'absolute',
    bottom: '0',
    left: '0',
    width: '200px',
    height: '80px',
    background: 'linear-gradient(90deg, transparent 0%, #637CEF 50%, transparent 100%)',
    clipPath: 'polygon(0% 70%, 15% 50%, 30% 65%, 45% 30%, 60% 45%, 75% 20%, 90% 35%, 100% 25%, 100% 100%, 0% 100%)',
    animationName: {
      '0%': { transform: 'translateX(-100px)' },
      '100%': { transform: 'translateX(0px)' },
    },
    animationDuration: '2s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
    animationDirection: 'alternate',
  },
  
  lineChartDots: {
    position: 'absolute',
    bottom: '0',
    left: '0',
    width: '100%',
    height: '100%',
  },
  
  lineChartDot: {
    position: 'absolute',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#637CEF',
    animationName: {
      '0%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
      '50%': { opacity: 1, transform: 'scale(1.2)' },
    },
    animationDuration: '1.2s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
  },
  
  dot1: {
    left: '10%',
    bottom: '30%',
    animationDelay: '0s',
  },
  
  dot2: {
    left: '35%',
    bottom: '60%',
    animationDelay: '0.3s',
  },
  
  dot3: {
    left: '60%',
    bottom: '40%',
    animationDelay: '0.6s',
  },
  
  dot4: {
    left: '85%',
    bottom: '70%',
    animationDelay: '0.9s',
  },
  
});

// ========================================
// InsightBadgeCard Component
// Figma-style insight pattern card
// ========================================
interface InsightBadgeCardProps {
  description: string;
  linkText?: string;
  onLinkClick?: () => void;
  badgeLabel?: string;
}

const InsightBadgeCard: React.FC<InsightBadgeCardProps> = ({
  description,
  linkText,
  onLinkClick,
  badgeLabel = 'INSIGHT',
}) => {
  const styles = useStyles();
  
  return (
    <div className={styles.insightBadgeCard}>
      <div className={styles.insightBadge}>
        <span className={styles.insightBadgeText}>{badgeLabel}</span>
      </div>
      <span className={styles.insightBadgeDescription}>{description}</span>
      {linkText && (
        <Link 
          className={styles.insightBadgeLink}
          onClick={onLinkClick}
        >
          {linkText}
        </Link>
      )}
    </div>
  );
};

interface InsightsReportProps {
  onBack?: () => void;
  formTitle?: string;
  responseCount?: number;
  isEmbedded?: boolean; // If true, hide back button and adjust layout for tab embedding
  surveyIntent?: string; // Survey intent from form metadata
}

export const InsightsReport: React.FC<InsightsReportProps> = ({ 
  onBack, 
  formTitle = "2025 Microsoft GCR Family Day",
  responseCount = 219,
  isEmbedded = false,
  surveyIntent
}) => {
  const styles = useStyles();
  
  // State for CSV upload and analysis
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<string | null>(null);
  const [insights, setInsights] = useState<AnalyzedInsights | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      setUploadedFile(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        setCsvData(text);
      };
      reader.readAsText(file);
    }
  };
  
  // Analyze CSV data when it's uploaded
  useEffect(() => {
    if (csvData) {
      setIsAnalyzing(true);
      setAnalysisError(null);
      
      (async () => {
        try {
          console.log('Starting LLM-powered CSV analysis...');
          console.log('CSV data length:', csvData.length);
          console.log('CSV data preview:', csvData.substring(0, 200));
          if (surveyIntent) {
            console.log('🎯 Survey Intent:', surveyIntent);
          }
          
          const analyzedData = await LLMSurveyAnalyzer.analyzeCSVData(csvData, surveyIntent as string);
          console.log('LLM Analysis complete:', analyzedData);
          console.log('Data overview:', analyzedData.dataOverview);
          console.log('Insights:', analyzedData.insights);
          console.log('Recommendations:', analyzedData.recommendations);
          
          setInsights(analyzedData);
          console.log('Insights state set, should re-render now');
        } catch (error) {
          console.error('Analysis error:', error);
          setAnalysisError(error instanceof Error ? error.message : 'Failed to analyze data');
        } finally {
          setIsAnalyzing(false);
        }
      })();
    }
  }, [csvData, surveyIntent]);
  
  // Helper function to get intent display info
  const getIntentDisplayInfo = (intent?: string) => {
    const intentMap: Record<string, { emoji: string; label: string; color: string }> = {
      'evaluation-retrospective': { emoji: '🔍', label: 'Evaluation & Retrospective', color: '#2196F3' },
      'choice-validation': { emoji: '⚖️', label: 'Choice Validation', color: '#4CAF50' },
      'preference-planning': { emoji: '📅', label: 'Preference & Planning', color: '#FF9800' },
      'funnel-diagnosis': { emoji: '📊', label: 'Funnel Diagnosis', color: '#E91E63' },
      'exploratory-discovery': { emoji: '🔬', label: 'Exploratory Discovery', color: '#9C27B0' },
      'mixed': { emoji: '🎯', label: 'Mixed Intent', color: '#607D8B' },
    };
    return intentMap[intent || ''] || { emoji: '📋', label: 'General Analysis', color: '#757575' };
  };
  
  // Show loading state while analyzing
  if (isAnalyzing) {
    const intentInfo = getIntentDisplayInfo(surveyIntent);
    return (
      <div className={styles.container}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: tokens.spacingVerticalL }}>
          {/* Dynamic Charts Loading Animation - Carousel */}
          <div className={styles.chartsLoader}>
            {/* Bar Chart - shows 0-2s */}
            <div className={mergeClasses(styles.chartCarouselItem, styles.chartItem1)}>
              <div className={styles.barChartLoader}>
                <div className={mergeClasses(styles.barLoaderItem, styles.bar1)} />
                <div className={mergeClasses(styles.barLoaderItem, styles.bar2)} />
                <div className={mergeClasses(styles.barLoaderItem, styles.bar3)} />
                <div className={mergeClasses(styles.barLoaderItem, styles.bar4)} />
              </div>
            </div>
            
            {/* Donut Chart - shows 2-4s */}
            <div className={mergeClasses(styles.chartCarouselItem, styles.chartItem2)}>
              <div className={styles.donutLoader}>
                <div className={styles.donutRing} />
                <div className={styles.donutSegment} />
                <div className={styles.donutSegment2} />
                <div className={styles.donutSegment3} />
                <div className={styles.donutCenter} />
              </div>
            </div>
            
            {/* Line Chart - shows 4-6s */}
            <div className={mergeClasses(styles.chartCarouselItem, styles.chartItem3)}>
              <div className={styles.lineChartLoader}>
                <div className={styles.lineChartPath} />
                <div className={styles.lineChartDots}>
                  <div className={mergeClasses(styles.lineChartDot, styles.dot1)} />
                  <div className={mergeClasses(styles.lineChartDot, styles.dot2)} />
                  <div className={mergeClasses(styles.lineChartDot, styles.dot3)} />
                  <div className={mergeClasses(styles.lineChartDot, styles.dot4)} />
                </div>
              </div>
            </div>
          </div>
          
          <Body1 style={{ color: tokens.colorNeutralForeground2, marginTop: '24px' }}>
            Analyzing survey data...</Body1>
          {surveyIntent && (
            <Badge 
              appearance="filled" 
              color="informative"
              style={{ 
                backgroundColor: intentInfo.color,
                color: 'white',
                fontSize: '13px',
                padding: '6px 14px'
              }}
            >
              {intentInfo.emoji} {intentInfo.label}
            </Badge>
          )}
        </div>
      </div>
    );
  }
  
  // Show error state if analysis failed
  if (analysisError) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Button
            appearance="subtle"
            icon={<ArrowLeft20Regular />}
            onClick={onBack}
            className={styles.backButton}
          >
            Back
          </Button>
          <Subtitle2>Survey Insights Report</Subtitle2>
        </div>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          flex: 1, 
          gap: tokens.spacingVerticalL, 
          padding: tokens.spacingHorizontalXXXL 
        }}>
          <Title3 style={{ color: tokens.colorPaletteRedForeground1 }}>
            Analysis Error
          </Title3>
          <Body1 style={{ color: tokens.colorNeutralForeground2 }}>
            {analysisError}
          </Body1>
          <Button appearance="primary" onClick={onBack}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }
  
  // Empty state - no CSV uploaded yet
  if (!csvData && !isAnalyzing) {
    return (
      <div className={styles.container}>
        {/* Empty state content */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          flex: 1, 
          gap: tokens.spacingVerticalXL,
          padding: tokens.spacingHorizontalXXXL 
        }}>
          <Document20Regular style={{ 
            fontSize: '64px', 
            color: tokens.colorNeutralForeground3 
          }} />
          
          <div style={{ 
            textAlign: 'center', 
            maxWidth: '500px',
            display: 'flex',
            flexDirection: 'column',
            gap: tokens.spacingVerticalM
          }}>
            <Title3>Upload CSV Data to Generate Insights</Title3>
            <Body1 style={{ color: tokens.colorNeutralForeground2 }}>
              Upload your survey response data in CSV format to automatically generate a comprehensive insights report with data visualizations and actionable recommendations.
            </Body1>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          
          <Button
            appearance="primary"
            size="large"
            icon={<ArrowUpload20Regular />}
            onClick={() => fileInputRef.current?.click()}
          >
            Upload CSV File
          </Button>

          {uploadedFile && (
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
              Selected: {uploadedFile.name}
            </Caption1>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container} style={isEmbedded ? { backgroundColor: 'transparent' } : {}}>
      {/* Scrollable content */}
      <div className={styles.scrollContent}>
        <div className={styles.contentContainer}>
          {/* Report title - only show if not embedded */}
          {!isEmbedded && (
            <div className={styles.reportHeader}>
              <Title1 className={styles.reportTitle}>
                Survey Insights Report — {formTitle}
              </Title1>
              <Caption1 className={styles.reportSubtitle}>
                Insights generated from {insights ? insights.responseCount : responseCount} valid responses. This report follows the standard principles of data integrity, traceable analysis, and action-oriented recommendations.
              </Caption1>
            </div>
          )}
          
          {/* Embedded report header - show when embedded and insights exist */}
          {isEmbedded && insights && (
            <div className={styles.embeddedReportHeader}>
              <div className={styles.embeddedReportHeaderLeft}>
                <div className={styles.embeddedReportTitle}>
                  {insights.title || `${formTitle} Insights Report`}
                </div>
                <div className={styles.embeddedReportDescription}>
                  Insights generated from {insights.responseCount} valid responses. This report follows the standard principles of data integrity, traceable analysis, and action-oriented recommendations.
                </div>
              </div>
              <div className={styles.dataSnapshot}>
                Data Sample: N={insights.responseCount}
              </div>
            </div>
          )}

        {/* If we have analyzed insights, show them */}
        {insights ? (
          <>
            {console.log('Rendering insights section with data:', insights)}
            {/* Section 1: Data Overview */}
            <div className={styles.sectionHeader}>
              <Title3 className={styles.sectionTitle}>Data Overview</Title3>
            </div>

            <div className={styles.overviewGrid}>
              {insights.dataOverview.map((metric, index) => {
                console.log(`Rendering metric ${index}:`, metric);
                console.log(`  - icon: "${metric.icon}", label: "${metric.label}", value: "${metric.value}"`);
                console.log(`  - description: "${metric.description}"`);
                
                // Determine if value is pure number/percentage (use larger font) or contains text (use smaller font)
                const isPureNumeric = /^[\d.,\s%/]+$/.test(String(metric.value));
                const valueClassName = isPureNumeric ? styles.cardValueLarge : styles.cardValue;

                return (
                  <Card key={index} className={styles.overviewCard}>
                    <div className={styles.cardContent}>
                      <Caption1 className={styles.cardLabel}>{metric.label}</Caption1>
                      <div className={valueClassName}>{metric.value}</div>
                      <Body1 className={styles.cardDescription}>
                        {metric.description}
                      </Body1>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Section 2: Further Insights */}
            <div className={styles.sectionHeader}>
              <Title3 className={styles.sectionTitle}>Further Insights</Title3>
            </div>

            <div className={styles.insightsContainer}>
              {insights.insights.map((insight) => {
                // Skip insights without data
                if (!insight.data && !insight.charts) {
                  return null;
                }
                
                // Use centralized question mapping utility
                const questions = extractQuestionTexts(insight.originalQuestion);
                const questionNumbers = parseQuestionNumbers(insight.originalQuestion);

                // Determine layout based on insight.layout field or content
                const isMultiChart = !!insight.charts && insight.charts.length > 1;
                const useTopDownLayout = insight.layout === 'top-down' || 
                  isMultiChart || 
                  !!insight.quotations || 
                  insight.data?.type === 'sentimentBar';

                // Debug logging
                console.log(`Insight ${insight.id}: "${insight.title}"`);
                console.log(`  layout: ${insight.layout || 'inferred'} → useTopDownLayout: ${useTopDownLayout}`);
                console.log(`  extracted questions:`, questions);
                console.log(`  question numbers:`, questionNumbers);
                if (questionNumbers.length === 0 && insight.originalQuestion) {
                  console.warn(`  ⚠️ No question numbers found for insight ${insight.id}. Check originalQuestion format.`);
                }
                
                // Determine if we have a table to show
                const hasTable = !!insight.tableData;
                const hasChart = !!insight.data;
                const hasQuotations = !!insight.quotations;
                
                return (
                  <Card key={insight.id} className={styles.insightCard}>
                    {/* ========== LEFT-RIGHT LAYOUT ========== */}
                    {/* Title+Explanation LEFT, Chart+Table RIGHT */}
                    {!useTopDownLayout && (
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'row', 
                        gap: tokens.spacingHorizontalXXL,
                        alignItems: 'flex-start'
                      }}>
                        {/* LEFT: Title + Explanation */}
                        <div style={{ flex: '0 0 45%', minWidth: 0 }}>
                          <div className={styles.insightTitleWrapper}>
                            <span className={styles.insightNumber}>{insight.id}.</span>
                            <div className={styles.insightTextBlock}>
                              <div className={styles.insightHeader}>
                                <span className={styles.insightTitle}>{insight.title}</span>
                                {questions.map((question, idx) => {
                                  const qNum = questionNumbers[idx];
                                  return qNum ? (
                                    <Tooltip key={idx} content={question} relationship="label" positioning="below">
                                      <span className={styles.questionBadge}>Q{qNum}</span>
                                    </Tooltip>
                                  ) : null;
                                })}
                              </div>
                              <Body1 style={{ color: tokens.colorNeutralForeground2, marginTop: tokens.spacingVerticalS }}>
                                {insight.explanation} {insight.summary}
                              </Body1>
                            </div>
                          </div>
                        </div>

                        {/* RIGHT: Chart Container with background */}
                        <div style={{ 
                          flex: '1 1 55%', 
                          minWidth: 0,
                          minHeight: '80px',
                          backgroundColor: '#f8fafc',
                          border: `1px solid ${tokens.colorNeutralStroke3}`,
                          borderRadius: '12px',
                          padding: '24px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: tokens.spacingVerticalM
                        }}>
                          {/* Chart */}
                          {hasChart && insight.data && insight.data.type && insight.data.items && (
                            <div style={{ width: '100%' }}>
                              {['sortedBar', 'groupedBar', 'rankedBar'].includes(insight.data.type) && 
                                renderBarChart(insight.data.type, insight.data.items)
                              }
                            </div>
                          )}
                          
                          {/* Table - only show in LEFT-RIGHT if no chart */}
                          {hasTable && !hasChart && insight.tableData && (
                            <div style={{ width: '100%' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ borderBottom: `2px solid ${tokens.colorNeutralStroke2}` }}>
                                    {insight.tableData.headers.map((header: string, idx: number) => (
                                      <th 
                                        key={idx}
                                        style={{ 
                                          padding: tokens.spacingVerticalM, 
                                          textAlign: idx === 0 ? 'left' : 'right', 
                                          fontWeight: tokens.fontWeightSemibold,
                                          color: tokens.colorNeutralForeground2
                                        }}
                                      >
                                        {header}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {insight.tableData.rows.map((row: string[], rowIdx: number) => (
                                    <tr 
                                      key={rowIdx} 
                                      style={{ 
                                        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                                        backgroundColor: rowIdx % 2 === 0 ? 'transparent' : tokens.colorNeutralBackground2
                                      }}
                                    >
                                      {row.map((cell: string, cellIdx: number) => (
                                        <td 
                                          key={cellIdx} 
                                          style={{ 
                                            padding: tokens.spacingVerticalM, 
                                            textAlign: cellIdx === 0 ? 'left' : 'right',
                                            fontWeight: cellIdx === 0 ? tokens.fontWeightRegular : tokens.fontWeightSemibold
                                          }}
                                        >
                                          {cell}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* ========== TOP-DOWN LAYOUT ========== */}
                    {/* Title+Explanation TOP, Chart+Table BOTTOM */}
                    {useTopDownLayout && (
                      <>
                        {/* TOP: Header + Title + Explanation */}
                        <div className={styles.insightTitleWrapper}>
                          <span className={styles.insightNumber}>{insight.id}.</span>
                          <div className={styles.insightTextBlock}>
                            <div className={styles.insightHeader}>
                              <span className={styles.insightTitle}>{insight.title}</span>
                              {questions.map((question, idx) => {
                                const qNum = questionNumbers[idx];
                                return qNum ? (
                                  <Tooltip key={idx} content={question} relationship="label" positioning="below">
                                    <span className={styles.questionBadge}>Q{qNum}</span>
                                  </Tooltip>
                                ) : null;
                              })}
                            </div>
                            <Body1 style={{ color: tokens.colorNeutralForeground2 }}>
                              {insight.explanation} {insight.summary}
                            </Body1>
                          </div>
                        </div>

                        {/* MULTI-CHART LAYOUT: Two charts side-by-side */}
                        {isMultiChart && insight.charts && (
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'row', 
                            gap: tokens.spacingHorizontalXXL,
                            alignItems: 'flex-start',
                            paddingTop: '24px',
                            paddingLeft: '48px'  // Align with title text
                          }}>
                            {insight.charts.map((chart, chartIdx) => {
                              // Pie/Donut charts need fixed height, bar charts can be responsive
                              const isPieOrDonut = chart.type === 'donutChart';
                              // Handle both LLM structures: chart.data.items (nested) and chart.items (flat)
                              const chartItems = (chart as unknown as { data?: { items?: ChartDataItem[] } }).data?.items || chart.items;
                              return (
                              <div 
                                key={chartIdx}
                                style={{ 
                                  flex: '1 1 50%', 
                                  minWidth: 0,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: tokens.spacingVerticalS
                                }}
                              >
                                {/* Chart caption */}
                                {chart.caption && (
                                  <Caption1 style={{ color: tokens.colorNeutralForeground2, fontWeight: tokens.fontWeightSemibold }}>
                                    {chart.caption}
                                  </Caption1>
                                )}
                                {/* Unified chart wrapper for consistent styling */}
                                <div 
                                  className={styles.chartWrapper}
                                  style={{ 
                                    height: isPieOrDonut ? '320px' : undefined,
                                    minHeight: isPieOrDonut ? undefined : '80px'
                                  }}
                                >
                                  {chart.type === 'groupedBar' && chartItems && (
                                    <FigmaHorizontalBarChart
                                      data={chartItems.map((item: ChartDataItem) => ({
                                        label: item.label || item.name || 'Unknown',
                                        value: typeof item.value === 'number' ? item.value : parseFloat(String(item.value)) || 0,
                                        color: item.color ? getVisualizationColor(item.color, 0) : getVisualizationColor('', 0),
                                      }))}
                                      noWrapper
                                    />
                                  )}
                                  {chart.type === 'donutChart' && chartItems && (
                                    <FluentPieChart
                                      data={chartItems.map((item: ChartDataItem) => ({
                                        label: item.label || item.name || 'Unknown',
                                        value: item.percentage || item.value,
                                        color: item.color || '',
                                        name: item.label || item.name || 'Unknown',
                                      }))}
                                      isDonut={true}
                                      showLabels={false}
                                      showLegend={true}
                                      width="100%"
                                      height={320}
                                    />
                                  )}
                                  {chart.type === 'rankedBar' && chartItems && (
                                    <FigmaHorizontalBarChart
                                      data={chartItems.map((item: ChartDataItem) => ({
                                        label: item.label || item.name || 'Unknown',
                                        value: typeof item.value === 'number' ? item.value : parseFloat(String(item.value)) || 0,
                                        color: item.color ? getVisualizationColor(item.color, 0) : getVisualizationColor('', 0),
                                      }))}
                                      noWrapper
                                    />
                                  )}
                                  {chart.type === 'sortedBar' && chartItems && (
                                    <FigmaHorizontalBarChart
                                      data={chartItems.map((item: ChartDataItem) => ({
                                        label: item.label || item.name || 'Unknown',
                                        value: typeof item.value === 'number' ? item.value : parseFloat(String(item.value)) || 0,
                                        color: item.color ? getVisualizationColor(item.color, 0) : getVisualizationColor('', 0),
                                      }))}
                                      noWrapper
                                    />
                                  )}
                                </div>
                              </div>
                            );
                            })}
                          </div>
                        )}

                        {/* BOTTOM: Single Chart and/or Table side-by-side (non-multi-chart) */}
                        {!isMultiChart && (hasChart || hasTable) && (
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'row', 
                            gap: tokens.spacingHorizontalXXL,
                            alignItems: 'flex-start',
                            paddingTop: '24px',
                            paddingLeft: '48px'  // Align with title text
                          }}>
                        {/* Chart Section - wrapped in unified chartWrapper */}
                        {hasChart && insight.data && (
                          <div 
                            className={styles.chartWrapper}
                            style={{ 
                              flex: hasTable ? '1 1 50%' : '1 1 100%', 
                              minWidth: 0, 
                              minHeight: insight.data.type === 'donutChart' ? '350px' : '80px',
                              height: insight.data.type === 'donutChart' ? '350px' : undefined
                            }}
                          >
                            {['sortedBar', 'groupedBar', 'rankedBar'].includes(insight.data.type || '') && insight.data.items &&
                              renderBarChart(insight.data.type || '', insight.data.items)
                            }
                            {insight.data.type === 'donutChart' && insight.data.items && (
                              <FluentPieChart
                                data={insight.data.items.map(item => ({
                                  label: item.label || 'Unknown',
                                  name: item.label || item.name || 'Unknown',
                                  value: item.percentage || item.value,
                                  color: item.color || '',
                                }))}
                                isDonut={true}
                                showLabels={true}
                                showLegend={true}
                                width="100%"
                                height={320}
                              />
                            )}
                            {insight.data.type === 'sentimentBar' && (
                              (() => {
                                const positiveItem = insight.data.items.find(i => (i.label || '').toLowerCase().includes('positive'));
                                const neutralItem = insight.data.items.find(i => (i.label || '').toLowerCase().includes('neutral'));
                                const negativeItem = insight.data.items.find(i => {
                                  const label = (i.label || '').toLowerCase();
                                  return label.includes('negative') || label.includes('constructive') || label.includes('mixed');
                                });
                                const positiveVal = positiveItem?.value || positiveItem?.percentage || 0;
                                const neutralVal = neutralItem?.value || neutralItem?.percentage || 0;
                                const negativeVal = negativeItem?.value || negativeItem?.percentage || 0;
                                const total = positiveVal + neutralVal + negativeVal;
                                return (
                                  <div style={{ 
                                    paddingLeft: '10px', 
                                    paddingRight: '10px',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                  }}>
                                    <FluentSentimentBarChart
                                      data={{
                                        positive: total > 0 ? Math.round((positiveVal / total) * 100) : 0,
                                        neutral: total > 0 ? Math.round((neutralVal / total) * 100) : 0,
                                        negative: total > 0 ? Math.round((negativeVal / total) * 100) : 0,
                                      }}
                                      label={insight.title}
                                    />
                                  </div>
                                );
                              })()
                            )}
                            {/* Likert Chart - Multiple Likert Questions (100% Stacked Bar) */}
                            {insight.data.type === 'likert' && insight.data.items && Array.isArray(insight.data.items) && (
                              <div style={{ 
                                width: '100%', 
                                minHeight: `${Math.max(400, insight.data.items.length * 35)}px`,
                                paddingLeft: '10px',
                                paddingRight: '10px',
                                boxSizing: 'border-box',
                              }}>
                                <div style={{ 
                                  width: '100%', 
                                  height: `${Math.max(400, insight.data.items.length * 35)}px` 
                                }}>
                                  {(() => {
                                    // Transform items to chartData format
                                    const chartData = insight.data.items.map((item: ChartDataItem) => ({
                                      label: item.label,
                                      'Strongly Disagree': item.stronglyDisagree || 0,
                                      'Disagree': item.disagree || 0,
                                      'Neutral': item.neutral || 0,
                                      'Agree': item.agree || 0,
                                      'Strongly Agree': item.stronglyAgree || 0,
                                    }));

                                    return (
                                      <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                          data={chartData}
                                          layout="vertical"
                                          margin={{ top: 20, right: 80, left: 250, bottom: 20 }}
                                          barCategoryGap="15%"
                                        >
                                          <CartesianGrid strokeDasharray="3 3" stroke={tokens.colorNeutralStroke2} horizontal={false} />
                                          <XAxis 
                                            type="number" 
                                            domain={[0, 100]}
                                            tickFormatter={(value) => `${value}%`}
                                            stroke={tokens.colorNeutralForeground3}
                                            tick={{ fill: tokens.colorNeutralForeground3, fontSize: 11 }}
                                          />
                                          <YAxis 
                                            type="category" 
                                            dataKey="label" 
                                            width={240}
                                            tick={{ fill: tokens.colorNeutralForeground2, fontSize: 11 }}
                                            interval={0}
                                          />
                                          <RechartsTooltip 
                                            formatter={(value: number | undefined) => `${value || 0}%`}
                                            cursor={{ fill: tokens.colorNeutralBackground3 }}
                                          />
                                          <Legend 
                                            wrapperStyle={{ paddingTop: '10px', fontSize: '12px' }}
                                            iconType="square"
                                          />
                                          <Bar 
                                            dataKey="Strongly Disagree" 
                                            stackId="a" 
                                            fill={dataVisualizationTokens.diverging.redToGreen5[0]}
                                            barSize={20}
                                          />
                                          <Bar 
                                            dataKey="Disagree" 
                                            stackId="a" 
                                            fill={dataVisualizationTokens.diverging.redToGreen5[1]}
                                            barSize={20}
                                          />
                                          <Bar 
                                            dataKey="Neutral" 
                                            stackId="a" 
                                            fill={dataVisualizationTokens.diverging.redToGreen5[2]}
                                            barSize={20}
                                          />
                                          <Bar 
                                            dataKey="Agree" 
                                            stackId="a" 
                                            fill={dataVisualizationTokens.diverging.redToGreen5[3]}
                                            barSize={20}
                                          />
                                          <Bar 
                                            dataKey="Strongly Agree" 
                                            stackId="a" 
                                            fill={dataVisualizationTokens.diverging.redToGreen5[4]}
                                            barSize={20}
                                          />
                                        </BarChart>
                                      </ResponsiveContainer>
                                    );
                                  })()}
                                </div>
                              </div>
                            )}
                            {/* Theme Cards for Category 7 (≤6 themes) */}
                            {insight.data.type === 'themeCards' && insight.themes && (
                              <FluentThemeCards
                                themes={insight.themes}
                                totalResponses={insight.data.items.reduce((sum, item) => sum + item.value, 0)}
                              />
                            )}
                            {/* Theme Table for Category 7 (>6 themes) */}
                            {insight.data.type === 'themeTable' && insight.themes && (
                              <FluentThemeTable
                                themes={insight.themes}
                                totalResponses={insight.data.items.reduce((sum, item) => sum + item.value, 0)}
                                showPercentage={true}
                              />
                            )}
                          </div>
                        )}

                        {/* Table Section */}
                        {hasTable && insight.tableData && (
                          <div style={{ flex: hasChart ? '1 1 50%' : '1 1 100%', minWidth: 0 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ borderBottom: `2px solid ${tokens.colorNeutralStroke2}` }}>
                                  {insight.tableData.headers.map((header: string, idx: number) => (
                                    <th 
                                      key={idx}
                                      style={{ 
                                        padding: tokens.spacingVerticalM, 
                                        textAlign: idx === 0 ? 'left' : 'right', 
                                        fontWeight: tokens.fontWeightSemibold,
                                        color: tokens.colorNeutralForeground2
                                      }}
                                    >
                                      {header}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {insight.tableData.rows.map((row: string[], rowIdx: number) => (
                                  <tr 
                                    key={rowIdx} 
                                    style={{ 
                                      borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                                      backgroundColor: rowIdx % 2 === 0 ? 'transparent' : tokens.colorNeutralBackground2
                                    }}
                                  >
                                    {row.map((cell: string, cellIdx: number) => (
                                      <td 
                                        key={cellIdx} 
                                        style={{ 
                                          padding: tokens.spacingVerticalM, 
                                          textAlign: cellIdx === 0 ? 'left' : 'right',
                                          fontWeight: cellIdx === 0 ? tokens.fontWeightRegular : tokens.fontWeightSemibold
                                        }}
                                      >
                                        {cell}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Quotations section (if present) */}
                    {hasQuotations && insight.quotations && (
                      <div className={styles.quotationsGridContainer} style={{ marginTop: tokens.spacingVerticalL }}>
                        <div className={`${styles.quotationColumn} ${styles.quotationColumnPositive}`}>
                          <div className={styles.quotationSectionHeader}>
                            <span className={styles.quotationSectionEmoji}>😊</span>
                            <span className={styles.quotationSectionTitle}>What people like</span>
                          </div>
                          {insight.quotations.positive && insight.quotations.positive.length > 0 ? (
                            insight.quotations.positive.slice(0, 8).map((quote, idx) => (
                              <div key={`pos-${idx}`} className={`${styles.quotationCard} ${styles.quotationCardPositive}`}>
                                <div className={styles.quotationTheme}>
                                  <span>{quote.theme || (typeof quote.text === 'string' ? quote.text : quote.text[0])}</span>
                                  {quote.count && quote.count > 1 && <span className={styles.quotationCounter}>+{quote.count}</span>}
                                </div>
                                {Array.isArray(quote.text) ? (
                                  quote.text.slice(0, 3).map((txt, txtIdx) => (
                                    <Body1 key={txtIdx} className={styles.quotationText}>{txt}</Body1>
                                  ))
                                ) : (
                                  <Body1 className={styles.quotationText}>{quote.text}</Body1>
                                )}
                              </div>
                            ))
                          ) : (
                            <Caption1 style={{ color: tokens.colorNeutralForeground3, fontStyle: 'italic' }}>No positive feedback</Caption1>
                          )}
                        </div>
                        <div className={`${styles.quotationColumn} ${styles.quotationColumnNegative}`}>
                          <div className={styles.quotationSectionHeader}>
                            <span className={styles.quotationSectionEmoji}>😟</span>
                            <span className={styles.quotationSectionTitle}>What people struggle with</span>
                          </div>
                          {insight.quotations.negative && insight.quotations.negative.length > 0 ? (
                            insight.quotations.negative.slice(0, 8).map((quote, idx) => (
                              <div key={`neg-${idx}`} className={`${styles.quotationCard} ${styles.quotationCardNegative}`}>
                                <div className={styles.quotationTheme}>
                                  <span>{quote.theme || (typeof quote.text === 'string' ? quote.text : quote.text[0])}</span>
                                  {quote.count && quote.count > 1 && <span className={styles.quotationCounter}>+{quote.count}</span>}
                                </div>
                                {Array.isArray(quote.text) ? (
                                  quote.text.slice(0, 3).map((txt, txtIdx) => (
                                    <Body1 key={txtIdx} className={styles.quotationText}>{txt}</Body1>
                                  ))
                                ) : (
                                  <Body1 className={styles.quotationText}>{quote.text}</Body1>
                                )}
                              </div>
                            ))
                          ) : (
                            <Caption1 style={{ color: tokens.colorNeutralForeground3, fontStyle: 'italic' }}>No areas for improvement</Caption1>
                          )}
                        </div>
                      </div>
                    )}
                      </>
                    )}
                  </Card>
                );
              })}
            </div>

            {/* Section 3: Recommendations */}
            <div className={styles.sectionHeader}>
              <Title3 className={styles.sectionTitle}>Actionable Recommendations</Title3>
            </div>

            <div className={styles.overviewGrid}>
              {insights.recommendations
                .sort((a, b) => {
                  const impactOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
                  return (impactOrder[a.impact || 'low'] || 2) - (impactOrder[b.impact || 'low'] || 2);
                })
                .map((rec) => (
                <Card key={rec.id} className={styles.overviewCard}>
                  <div className={styles.cardContent}>
                    <Caption1 style={{ 
                      color: tokens.colorBrandForeground1,
                      fontWeight: tokens.fontWeightSemibold,
                      marginBottom: tokens.spacingVerticalS
                    }}>
                      {rec.label}
                    </Caption1>
                    <Subtitle2 style={{ marginBottom: tokens.spacingVerticalS }}>
                      {rec.title}
                    </Subtitle2>
                    <Body1 className={styles.cardDescription}>
                      {rec.description}
                    </Body1>
                    {rec.impact && (
                      <Caption1 style={{
                        marginTop: tokens.spacingVerticalS,
                        padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
                        backgroundColor: rec.impact === 'high' 
                          ? 'rgba(115, 103, 240, 0.1)'
                          : rec.impact === 'medium'
                          ? 'rgba(255, 185, 105, 0.15)'
                          : tokens.colorNeutralBackground3,
                        color: rec.impact === 'high' 
                          ? '#7367F0'
                          : rec.impact === 'medium'
                          ? '#FF9F43'
                          : tokens.colorNeutralForeground2,
                        borderRadius: tokens.borderRadiusSmall,
                        display: 'inline-flex',
                        alignSelf: 'flex-start',
                        fontSize: tokens.fontSizeBase100,
                        fontWeight: tokens.fontWeightSemibold,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}>
                        {rec.impact.charAt(0).toUpperCase() + rec.impact.slice(1)} Impact
                      </Caption1>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </>
        ) : (
          <>
            {/* Fallback to default hardcoded data if no CSV analysis */}
            {/* Section 1: Data Overview */}
        <div className={styles.sectionHeader}>
          <Title3 className={styles.sectionTitle}>Data Overview</Title3>
          <Caption1 className={styles.sectionSubtitle}>SECTION 1</Caption1>
        </div>

        <div className={styles.overviewGrid}>
          {/* Overall satisfaction */}
          <Card className={styles.overviewCard}>
            <div className={styles.cardContent}>
              <div className={styles.cardIcon}>
                <ChartMultiple20Regular />
              </div>
              <Caption1 className={styles.cardLabel}>Overall satisfaction</Caption1>
              <div className={styles.cardValue}>4.4 / 5</div>
              <Body1 className={styles.cardDescription}>
                Participants rated the 2025 Family Day experience very positively overall.
              </Body1>
            </div>
          </Card>

          {/* Attendance rate */}
          <Card className={styles.overviewCard}>
            <div className={styles.cardContent}>
              <div className={styles.cardIcon}>
                <PeopleCommunity20Regular />
              </div>
              <Caption1 className={styles.cardLabel}>Attendance rate</Caption1>
              <div className={styles.cardValue}>96% attended</div>
              <Body1 className={styles.cardDescription}>
                Almost all registered employees and families showed up on the event day.
              </Body1>
            </div>
          </Card>

          {/* Top 2025 option */}
          <Card className={styles.overviewCard}>
            <div className={styles.cardContent}>
              <div className={styles.cardIcon}>
                <Trophy20Regular />
              </div>
              <Caption1 className={styles.cardLabel}>Top 2025 option</Caption1>
              <div className={styles.cardValue}>66% UBR</div>
              <Body1 className={styles.cardDescription}>
                Universal Beijing Resort attracted about two-thirds of all participants.
              </Body1>
            </div>
          </Card>

          {/* Next-year preference */}
          <Card className={styles.overviewCard}>
            <div className={styles.cardContent}>
              <div className={styles.cardIcon}>
                <DataBarVertical20Regular />
              </div>
              <Caption1 className={styles.cardLabel}>Next-year preference</Caption1>
              <div className={styles.cardValue}>71% theme parks</div>
              <Body1 className={styles.cardDescription}>
                Theme-park style activities and early autumn timing are most preferred for next year.
              </Body1>
            </div>
          </Card>
        </div>

        {/* Key insight summary */}
        <InsightBadgeCard
          description="Participants were highly engaged: almost everyone attended and rated the event highly, with strong demand for theme-park style experiences and early-autumn timing."
        />

        {/* Section 2: Further Insights */}
        <div className={styles.sectionHeader}>
          <Title3 className={styles.sectionTitle}>Further Insights</Title3>
          <Caption1 className={styles.sectionSubtitle}>SECTION 2</Caption1>
        </div>

        <div className={styles.insightsContainer}>
          {/* Insight 1 */}
          <Card className={styles.insightCard}>
            <div className={styles.insightTitleWrapper}>
              <span className={styles.insightNumber}>1.</span>
              <div className={styles.insightTextBlock}>
                <div className={styles.insightHeader}>
                  <span className={styles.insightTitle}>Immersive story-based venues deliver the highest satisfaction while logistics issues lower scores for certain options</span>
                </div>
                <Body1 style={{ color: tokens.colorNeutralForeground2 }}>
                  Original question: "Which event option did you participate in?" + "Please rate this event overall (including aspects such as the event time, location, and content)".
                </Body1>
              </div>
            </div>

            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th className={styles.tableHeader}>Event option</th>
                  <th className={styles.tableHeader}>Avg. rating</th>
                  <th className={styles.tableHeader}>vs overall (4.4)</th>
                  <th className={styles.tableHeader}>Sample size</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={styles.tableCell}>Universal Beijing Resort</td>
                  <td className={styles.tableCell}>4.5</td>
                  <td className={styles.tableCell}>+0.1 vs overall</td>
                  <td className={styles.tableCell}>N = 144</td>
                </tr>
                <tr>
                  <td className={styles.tableCell}>Unique Dream of Red Mansion Land of Dramas (Langfang)</td>
                  <td className={styles.tableCell}>4.6</td>
                  <td className={styles.tableCell}>+0.2 vs overall</td>
                  <td className={styles.tableCell}>N = 30</td>
                </tr>
                <tr>
                  <td className={styles.tableCell}>Happy Liaozhai - Three Lives Immersion Edition - Mahua Funage</td>
                  <td className={styles.tableCell}>4.1</td>
                  <td className={styles.tableCell}>-0.4 vs overall</td>
                  <td className={styles.tableCell}>N = 17</td>
                </tr>
                <tr>
                  <td className={styles.tableCell}>SoReal Sci-Fi Park</td>
                  <td className={styles.tableCell}>3.9</td>
                  <td className={styles.tableCell}>-0.6 vs overall</td>
                  <td className={styles.tableCell}>N = 28</td>
                </tr>
              </tbody>
            </table>

            <InsightBadgeCard
              description="Overall satisfaction is strong (4.4 / 5). Red Mansion and UBR are slightly above the average, while SoReal and Happy Liaozhai score modestly lower. This points to immersive story-based venues as key drivers of delight, and suggests reviewing experience design and logistics for the lower-scoring options."
            />
          </Card>

          {/* Insight 2 */}
          <Card className={styles.insightCard}>
            <div className={styles.insightTitleWrapper}>
              <span className={styles.insightNumber}>2.</span>
              <div className={styles.insightTextBlock}>
                <div className={styles.insightHeader}>
                  <span className={styles.insightTitle}>Theme-park style activities and early-autumn timing emerge as the strongest preferences for next year's Family Day</span>
                </div>
                <Body1 style={{ color: tokens.colorNeutralForeground2 }}>
                  Original question: "What type of activities would you prefer for next year's Family Day?" + "When would you prefer to hold next year's Family Day?"
                </Body1>
              </div>
            </div>

            <InsightBadgeCard
              description="71% prefer theme-park style activities, and early-autumn timing is favored by the majority. Consider prioritizing immersive, family-friendly theme park experiences and scheduling the event in September or early October."
            />
          </Card>
        </div>
          </>
        )}
        </div>
      </div>
    </div>
  );
};
